import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  type AutocompleteProps,
  Checkbox,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { Landmark } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { OrganisationSelectFragment } from '@graphql/__generated__/graphql';
import {
  RaAutoComplete,
  type RaAutocompleteProps,
} from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { TeamAvatar } from '@modules/_shared/components/TeamAvatar';
import { getOrganisationLogoUrl } from '@modules/_shared/utils/organisationLogo';
import { useLocale } from '@shared/hooks/locale';

export const ORGANISATION_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment OrganisationSelect on organisations {
    id
    name
    website
    organisation_images(
      order_by: [{ is_primary: desc }, { order_nr: asc }]
      limit: 1
    ) {
      image {
        id
        url
      }
    }
    teams(
      where: { logo_image_id: { _is_null: false } }
      order_by: { created_at: asc }
      limit: 1
    ) {
      id
      logo {
        url
      }
    }
  }
`);

export const SEARCH_ORGANISATIONS = gql(/* GraphQL */ `
  query SearchOrganisations($q: String!) {
    organisations_search(args: { search: $q }, limit: 10) {
      id
      ...OrganisationSelect
    }
  }
`);

const GET_DEFAULT_ORGANISATION = gql(/* GraphQL */ `
  query GetDefaultOrganisation($ids: [uuid!]!) {
    selected_organisations: organisations(where: { id: { _in: $ids } }) {
      id
      ...OrganisationSelect
    }

    initial_organisations: organisations(
      where: { id: { _nin: $ids } }
      limit: 20
      order_by: { name: asc }
    ) {
      id
      ...OrganisationSelect
    }
  }
`);

type OrganisationSelectProps = Pick<
  RaAutocompleteProps<
    OrganisationSelectFragment & { isRestricted?: true },
    false
  >,
  | 'InputProps'
  | 'autoFocus'
  | 'disabled'
  | 'loading'
  | 'sx'
  | 'onClose'
  | 'InputComponent'
> &
  Pick<
    AutocompleteProps<
      OrganisationSelectFragment & { isRestricted?: true },
      false,
      false,
      false
    >,
    'slots' | 'slotProps'
  > & {
    organisationId?: string | null;
    onChange?: (organisationId: string | null) => void;
    condensed?: boolean;
    /** Prefills the search input and loads matching organisations on mount. */
    initialSearchQuery?: string;
  };

export const OrganisationSelectItem: React.FC<
  React.HTMLAttributes<HTMLLIElement> & {
    organisationItem: OrganisationSelectFragment;
    condensed?: boolean;
    selectable?: boolean;
    selected?: boolean;
  }
> = ({
  organisationItem,
  condensed = false,
  selectable = false,
  selected,
  ...props
}) => {
  const logoUrl = getOrganisationLogoUrl(organisationItem);

  return (
    <ListItem {...props}>
      {selectable && (
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={selected}
            tabIndex={-1}
            disableRipple
          />
        </ListItemIcon>
      )}
      <ListItemAvatar>
        <TeamAvatar logoUrl={logoUrl} size={condensed ? 32 : 40} />
      </ListItemAvatar>
      <ListItemText
        primary={organisationItem.name}
        secondary={organisationItem.website}
      />
    </ListItem>
  );
};

export const OrganisationSelect = ({
  onChange,
  InputProps,
  organisationId,
  autoFocus,
  disabled,
  loading,
  sx,
  condensed,
  initialSearchQuery,
  ...autoCompleteProps
}: OrganisationSelectProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<OrganisationSelectFragment[]>([]);
  const [inputValue, setInputValue] = useState('');
  const prevOrganisationId = useRef<string | undefined | null>(organisationId);
  const [selectedValue, setSelectedValue] = useState<
    (OrganisationSelectFragment & { isRestricted?: true }) | null
  >(null);

  const { data: defaultData, loading: loadingDefaultOrganisation } = useQuery(
    GET_DEFAULT_ORGANISATION,
    {
      variables: { ids: organisationId ? [organisationId] : [] },
    },
  );

  const updateOptions = useMemo(
    () =>
      (
        newValue: OrganisationSelectFragment | null,
        initialOrgs?: OrganisationSelectFragment[],
      ) => {
        const combined =
          newValue == null
            ? initialOrgs ?? []
            : [newValue, ...(initialOrgs ?? [])];

        const uniqueCombined = combined.filter(
          (org, idx, arr) => arr.findIndex(o => o.id === org.id) === idx,
        );
        setOptions(uniqueCombined);
      },
    [],
  );

  const getOptionLabel = useCallback(
    (option: OrganisationSelectFragment & { isRestricted?: true }) => {
      if (option.isRestricted) {
        return t('Restricted organisation');
      }

      return option.name ?? t('Untitled organisation');
    },
    [t],
  );

  useEffect(() => {
    let selectedItem:
      | (OrganisationSelectFragment & { isRestricted?: true })
      | null = null;

    if (defaultData == null) {
      return;
    }

    if (defaultData.selected_organisations.length > 0) {
      selectedItem = defaultData.selected_organisations[0];
    } else if (organisationId != null) {
      // We have an organisation but we don't have access to it (permission denied).
      selectedItem = {
        id: organisationId,
        name: t('Restricted organisation'),
        isRestricted: true,
        organisation_images: [],
        teams: [],
      };
    }
    setSelectedValue(selectedItem);
    if (selectedItem != null) {
      setInputValue(getOptionLabel(selectedItem));
    }
    updateOptions(selectedItem, defaultData?.initial_organisations);

    if (autoFocus) {
      setOpen(true);
    }
  }, [
    defaultData,
    organisationId,
    updateOptions,
    autoFocus,
    t,
    getOptionLabel,
  ]);

  if (prevOrganisationId.current !== organisationId) {
    prevOrganisationId.current = organisationId;

    if (organisationId == null) {
      setSelectedValue(null);
      setInputValue(initialSearchQuery?.trim() ?? '');
    }
  }

  const [searchOrganisations, { loading: searchLoading, error }] = useLazyQuery(
    SEARCH_ORGANISATIONS,
    { notifyOnNetworkStatusChange: true },
  );

  useEffect(() => {
    const query = initialSearchQuery?.trim() ?? '';
    if (!query || organisationId != null || selectedValue != null) {
      return;
    }

    setInputValue(query);
    searchOrganisations({ variables: { q: query } }).then(response => {
      setOpen(true);
      setOptions(response.data?.organisations_search ?? []);
    });
  }, [initialSearchQuery, organisationId, searchOrganisations, selectedValue]);

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        if (!value) {
          setOptions(defaultData?.initial_organisations ?? []);
          setOpen(true);
          return;
        }
        searchOrganisations({
          variables: {
            q: value,
          },
        }).then(response => {
          setOpen(true);
          setOptions(response.data?.organisations_search ?? []);
        });
      }, 275),
    [searchOrganisations, defaultData],
  );

  useEffect(() => () => debouncedSearch.clear(), [debouncedSearch]);

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: (OrganisationSelectFragment & { isRestricted?: true }) | null,
    ) => {
      setSelectedValue(value);
      onChange?.(value?.id ?? null);
      if (value != null) {
        setInputValue(getOptionLabel(value));
      } else {
        setInputValue(initialSearchQuery?.trim() ?? '');
        setOptions(defaultData?.initial_organisations ?? []);
        setOpen(true);
      }
    },
    [getOptionLabel, initialSearchQuery, onChange, defaultData],
  );

  const renderOption = useCallback(
    (
      { key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: any },
      option: OrganisationSelectFragment & { isRestricted?: true },
    ) => (
      <OrganisationSelectItem
        organisationItem={option}
        key={key}
        {...props}
        condensed={condensed}
      />
    ),
    [condensed],
  );

  return (
    <RaAutoComplete<OrganisationSelectFragment & { isRestricted?: true }>
      {...autoCompleteProps}
      multiple={false}
      sx={sx}
      InputProps={{
        ...InputProps,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={Landmark} />
        ),
        placeholder: InputProps?.placeholder ?? t('Search organisations'),
      }}
      open={open}
      onOpen={() => {
        updateOptions(selectedValue, defaultData?.initial_organisations);
        setOpen(true);
      }}
      setOpen={setOpen}
      options={options}
      setOptions={setOptions}
      allowEmptySearch
      loading={loading || searchLoading || loadingDefaultOrganisation}
      value={selectedValue}
      error={error != null}
      autoFocus={autoFocus}
      debouncedSearch={debouncedSearch}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInputValue);
        } else if (reason === 'reset' && newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      onChange={handleChange}
      disabled={disabled || selectedValue?.isRestricted}
      getOptionLabel={getOptionLabel}
      tooltip={
        selectedValue?.isRestricted
          ? t("You don't have access to this organisation")
          : undefined
      }
      renderOption={renderOption}
    />
  );
};
